import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';
import WarningIcon from '@mui/icons-material/Warning';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { Code, GlassIconButton, LoadingOverlay } from 'dashboard/components/common';

const copy = (text) => {
  try {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  } catch (_) {
    toast.error('Copy failed');
  }
};

/**
 * The subdomain part of a hostname, or '' when it is a bare domain.
 * Two labels is an apex (acme.com). Anything longer has a subdomain in front
 * — with the known exception of multi-label TLDs like acme.co.uk, which this
 * cannot tell apart without a public-suffix list; the copy below hedges.
 */
export const subdomainLabel = (host = '') => {
  const parts = String(host).split('.');
  return parts.length > 2 ? parts.slice(0, -2).join('.') : '';
};

function StatusBadge({ status }) {
  if (status === 'verified') {
    return <Chip size='small' color='success' icon={<CheckCircleIcon fontSize='small' />} label='Verified' />;
  }
  if (status === 'failed') {
    return <Chip size='small' color='error' icon={<WarningIcon fontSize='small' />} label='Failed' />;
  }
  return <Chip size='small' color='warning' label='Pending' />;
}

/**
 * The brand's one primary domain. Owner adds the apex (e.g. `acme.com`),
 * publishes the TXT verification record, then clicks Verify. Once
 * verified the brand can map subdomains beneath it in `<SubdomainRulesCard />`.
 *
 * Exposes `onPrimaryChanged(domain | null)` so the parent can
 * enable / refresh the subdomain card when the primary flips.
 */
/**
 * A bare hostname from whatever someone typed.
 *
 * Brand creation asks for a domain with `https://example.com` as its
 * placeholder, so what arrives is often a URL. This screen wants the host on
 * its own — a scheme or a trailing path in a DNS name never matches anything.
 */
const bareDomain = (raw) => String(raw || '')
  .trim()
  .toLowerCase()
  .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
  .replace(/\/.*$/, '')
  .replace(/:\d+$/, '')
  .replace(/\.$/, '');

export default function PrimaryDomainCard({ brandId, onPrimaryChanged }) {
  const [row, setRow] = useState(null);
  const [newDomain, setNewDomain] = useState('');
  const [loading, setLoading] = useState(false);
  // Kept on screen rather than only toasted: a DNS failure is something you
  // act on with the record open in another tab, not a message that should
  // vanish after four seconds.
  const [checkError, setCheckError] = useState('');
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    if (!brandId) return Promise.resolve();
    setLoading(true);
    return API.listBrandDomains({ brand_id: brandId })
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        const primary = list[0] || null;
        setRow(primary);
        onPrimaryChanged?.(primary);

        /**
         * Offer back the domain given when the brand was created.
         *
         * Creating a brand asks for a domain and stores it on the brand
         * itself; this screen keeps verified domains in their own records and
         * knew nothing about it. So someone typed their domain during signup
         * and arrived here to an empty box asking for it again.
         *
         * Filled in rather than added outright: adding one starts a
         * verification that expects a DNS record, and that should follow a
         * deliberate click. It is an ordinary field — correct it, or replace
         * it, before pressing Add.
         */
        if (primary) return null;
        return API.getBrandById({ brand_id: brandId })
          .then((brand) => {
            const known = bareDomain(brand?.domain);
            if (known) setNewDomain((current) => current || known);
          })
          .catch(() => null);
      })
      .catch((err) => toast.error(err?.message || 'Failed to load domain', { toastId: 'primary-domain-load' }))
      .finally(() => setLoading(false));
  }, [brandId, onPrimaryChanged]);

  useEffect(() => { load(); }, [load]);

  const addPrimary = () => {
    const value = newDomain.trim();
    if (!value) return;
    setBusy('add');
    API.addBrandDomain({ brand_id: brandId, domain: value })
      .then(() => { setNewDomain(''); toast.success('Primary domain added'); load(); })
      .catch((err) => toast.error(err?.message || 'Failed to add domain'))
      .finally(() => setBusy(null));
  };

  const verify = () => {
    if (!row) return;
    setBusy('verify');
    setCheckError('');
    API.verifyBrandDomain({ brand_id: brandId, _id: row._id })
      .then((updated) => {
        if (updated?.status === 'verified') {
          toast.success(`${updated.domain} verified`);
        } else {
          const reason = updated?.failure_reason || 'The TXT record was not found.';
          setCheckError(reason);
          toast.warning(reason);
        }
        load();
      })
      .catch((err) => {
        const reason = err?.message || 'Verification failed';
        setCheckError(reason);
        toast.error(reason);
      })
      .finally(() => setBusy(null));
  };

  const remove = () => {
    if (!row) return;
    if (!window.confirm(
      `Remove ${row.domain}? All subdomain rules under it will stop routing.`,
    )) return;
    setBusy('delete');
    API.deleteBrandDomain({ brand_id: brandId, _id: row._id })
      .then(() => { toast.success('Removed'); load(); })
      .catch((err) => toast.error(err?.message || 'Failed to remove'))
      .finally(() => setBusy(null));
  };

  return (
    <Box>
      <Box mb='14px'>
        <Typography variant='h6' fontWeight='700'>1. Verify your domain</Typography>
        <Typography variant='body2' color='text.secondary'>
          One domain per brand. Publish a single TXT record to prove you own it — this
          proves ownership only, it does not route any traffic yet.
        </Typography>
      </Box>

      <LoadingOverlay loading={loading && !row} minHeight={120}>
        {!row ? (
          <Box>
            <Typography variant='body2' fontWeight='600' mb='6px'>Add your domain</Typography>
            <Typography variant='body2' color='text.secondary' mb='8px'>
              Use the bare domain (<Code>acme.com</Code>) if you want the whole site here,
              or just a subdomain (<Code>shop.acme.com</Code>) to leave the rest of your
              domain where it is. Either way you verify only the one you enter.
            </Typography>
            <Stack direction='row' spacing='8px'>
              <TextField
                size='small'
                placeholder='acme.com'
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addPrimary(); }}
                fullWidth
              />
              <Button
                variant='contained'
                onClick={addPrimary}
                disabled={!newDomain.trim() || busy === 'add'}
              >
                {busy === 'add' ? 'Adding…' : 'Add'}
              </Button>
            </Stack>
            <Typography variant='caption' color='text.secondary' mt='8px' display='block'>
              Use the apex (e.g. <em>acme.com</em>). Subdomains like <em>shop</em>, <em>blog</em>, <em>app</em> are mapped in the next section.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              border: '1px solid', borderColor: 'divider',
              borderRadius: '12px', p: '14px',
            }}
          >
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              flexWrap='wrap'
              gap='10px'
              mb='10px'
            >
              <Stack direction='row' spacing='8px' alignItems='center'>
                <Typography fontWeight='600'>{row.domain}</Typography>
                <StatusBadge status={row.status} />
              </Stack>
              <Stack direction='row' spacing='4px'>
                {/* Spinner in place of the icon while the lookup runs, so the
                    click clearly did something. A DNS check can take a second
                    or two and the old button just sat there looking ignored. */}
                <GlassIconButton
                  label={busy === 'verify' ? 'Checking DNS…' : 'Re-check DNS'}
                  size='small'
                  onClick={verify}
                  disabled={busy === 'verify'}
                >
                  {busy === 'verify'
                    ? <CircularProgress size={17} thickness={5} />
                    : <ReplayIcon fontSize='small' />}
                </GlassIconButton>
                <Tooltip title='Remove'>
                  <IconButton size='small' color='error' onClick={remove} disabled={busy === 'delete'}>
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            {checkError && (
              <Alert
                severity='warning'
                onClose={() => setCheckError('')}
                sx={{ borderRadius: '10px', mb: '12px' }}
              >
                <Typography variant='body2' fontWeight='600' mb='2px'>
                  Not verified yet
                </Typography>
                <Typography variant='body2'>{checkError}</Typography>
                <Typography variant='caption' color='text.secondary' display='block' mt='6px'>
                  If you have just published the record, give it time. The TTL on a DNS
                  record is how long resolvers around the world are allowed to keep the
                  old answer cached — a 5 minute TTL means it can take up to 5 minutes
                  (often longer on the first publish, since some resolvers cache a
                  "no such record" reply) before the change is visible everywhere.
                  Then check again.
                </Typography>
              </Alert>
            )}

            {row.status !== 'verified' && (
              <Box sx={{ bgcolor: 'action.hover', borderRadius: '8px', p: '12px' }}>
                <Typography variant='body2' fontWeight='600' mb='8px'>To verify:</Typography>
                <Typography variant='body2' mb='6px'>
                  <strong>1.</strong> Publish a <Code>TXT</Code> record:
                </Typography>
                <Box ml='14px' mb='10px'>
                  {/* Offer the RELATIVE host. Most DNS panels (Google Domains,
                      Cloudflare, Namecheap…) append the zone to whatever is
                      typed here, so pasting the full `_techproone.acme.com`
                      silently creates `_techproone.acme.com.acme.com` — the
                      record looks published, resolves nowhere, and verification
                      fails with "no TXT record" while the value is plainly
                      correct on screen. */}
                  <Stack direction='row' alignItems='center' spacing='8px' mb='4px'>
                    <Typography variant='caption' color='text.secondary' minWidth='60px'>Host</Typography>
                    <Code>_techproone</Code>
                    <IconButton size='small' onClick={() => copy('_techproone')}>
                      <ContentCopyIcon fontSize='small' />
                    </IconButton>
                  </Stack>
                  <Typography variant='caption' color='text.secondary' display='block' mb='6px'>
                    Just <Code>_techproone</Code> — most providers add{' '}
                    <Code>{row.domain}</Code> for you. If yours wants the full name,
                    use <Code>_techproone.{row.domain}</Code>.
                  </Typography>
                  <Stack direction='row' alignItems='center' spacing='8px'>
                    <Typography variant='caption' color='text.secondary' minWidth='60px'>Value</Typography>
                    <Code>{row.verification_token}</Code>
                    <IconButton size='small' onClick={() => copy(row.verification_token)}>
                      <ContentCopyIcon fontSize='small' />
                    </IconButton>
                  </Stack>
                </Box>
                {/* The per-subdomain CNAME used to be step 2 here, as a generic
                    "point www, shop…" instruction. It now lives on each rule in
                    Subdomain routing, where the exact hostname is known — this
                    card only has to get ownership proven. */}
                <Typography variant='body2' mb='6px'>
                  <strong>2.</strong> Click the refresh button above. Once it verifies, the
                  records that actually route traffic appear below.
                </Typography>
                {row.failure_reason && row.status === 'failed' && (
                  <Typography variant='caption' color='error.main' mt='8px' display='block'>
                    Last check: {row.failure_reason}
                  </Typography>
                )}
              </Box>
            )}

            {row.status === 'verified' && (
              <Typography variant='body2' color='text.secondary'>
                Ownership verified — <Code>{row.domain}</Code> and any subdomain of it
                belong to this brand.
              </Typography>
            )}
          </Box>
        )}
      </LoadingOverlay>
    </Box>
  );
}
